// LLM tab — lists the server's configured LLM targets and lets you
// flip the active one. Status panel shows per-target reachability +
// latency so you can see which upstream is healthy without leaving
// the admin overlay.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type LlmStatusReport, type LlmTarget } from "../../lib/rpc";

interface LlmTabProps {
  notify: (msg: string) => void;
}

export function AdminLlmTab({ notify }: LlmTabProps) {
  const [targets, setTargets] = useState<LlmTarget[] | null>(null);
  const [status, setStatus] = useState<LlmStatusReport | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [t, s] = await Promise.all([
        api.adminLlmTargets(),
        api.adminLlmStatus().catch(() => null),
      ]);
      setTargets(t);
      setStatus(s);
    } catch (e: any) {
      notify(e?.message || "Couldn't load LLM info");
      setTargets([]);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const activate = async (name: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.adminLlmSwitch(name);
      notify(`Switched to ${name}`);
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Switch failed");
    } finally {
      setBusy(false);
    }
  };

  const statusFor = (name: string) => status?.targets?.find((t) => t.name === name);

  return (
    <div className="admin-card">
      <h3>LLM targets</h3>
      <p className="admin-card-sub">
        Configured upstream endpoints. Flip the active one — new turns route there.
      </p>
      {targets === null ? (
        <p className="admin-loading">Loading…</p>
      ) : targets.length === 0 ? (
        <p className="admin-empty">No targets configured.</p>
      ) : (
        <div>
          {targets.map((t) => {
            const s = statusFor(t.name);
            return (
              <div key={t.name} className="admin-llm-row">
                <span className="admin-llm-icon" data-active={!!t.active}>
                  <Icon name="sparkle" size={15} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="admin-llm-name">
                    {t.label || t.name}
                    {t.active && <span className="admin-pill admin-pill-admin">active</span>}
                    {s && (
                      <span className={`admin-pill ${s.ok ? "admin-pill-2fa" : "admin-pill-force"}`}>
                        {s.ok ? `${s.latencyMs ?? "?"}ms` : "unreachable"}
                      </span>
                    )}
                  </div>
                  <div className="admin-llm-meta">
                    <span className="admin-user-mono">{t.name}</span>
                    {t.model ? ` · ${t.model}` : ""}
                    {t.baseUrl ? ` · ${t.baseUrl}` : ""}
                    {s && !s.ok && s.error ? ` · ${s.error.slice(0, 80)}` : ""}
                  </div>
                </div>
                <button
                  className="admin-mini-btn"
                  disabled={busy || !!t.active}
                  onClick={() => activate(t.name)}
                  type="button"
                >
                  {t.active ? "active" : "activate"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
